/**
 * The next-morning card on Home while an earlier day is unreleased: what's
 * waiting, "Review now" (the Board, which groups it as "From [day]") and
 * "Release all ready".
 */
import { ArrowRight, Check } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { AssistantAvatar } from "@/features/agents/components/AssistantAvatar";
import { agentLabelAt, type AgentScope } from "@/features/agents/types";

import { timeActions } from "../actions";
import { useTimeEntries } from "../queries";
import type { UnreleasedDay } from "../types";
import { formatHours, totalHours } from "../utils";
import { carryTitle, isOpen, readyToRelease } from "./homeModel";
import { ReviewRow } from "./ReviewRow";

const MAX_ROWS = 6;
const NONE = new Set<string>();

type Props = {
  days: UnreleasedDay[];
  practice: AgentScope;
  onReview: () => void;
};

export const CarryOverCard: FC<Props> = ({ days, practice, onReview }) => {
  const from = days[0]?.work_date ?? "";
  const to = days[days.length - 1]?.work_date ?? "";
  const list = useTimeEntries(from, to, Boolean(from));
  const open = (list.data?.entries ?? []).filter(isOpen);
  const ready = readyToRelease(open, NONE);
  const count = days.reduce((n, d) => n + d.count, 0);
  const hours = days.reduce((n, d) => n + d.hours, 0);
  if (!days.length) return null;

  const single = days.length === 1;
  return (
    <div
      className="shrink-0 overflow-hidden rounded-xl border border-line-2"
      data-testid="carry-over"
    >
      <div className="flex flex-col gap-2 bg-warn-soft px-4 pt-3.5 pb-3">
        <span className="flex items-center gap-2">
          <AssistantAvatar
            agent="practice"
            seed={`practice:${practice.id}`}
            size="sm"
          />
          <span className="text-base font-medium">
            {agentLabelAt("assistant", "practice")}
          </span>
        </span>
        <span className="text-base font-medium">{carryTitle(days)}</span>
        <p className="text-base leading-5">
          {count} {count === 1 ? "entry" : "entries"} ·{" "}
          <span className="tabular-nums">{formatHours(hours)}</span> still
          waiting. They're easiest to check while the day is fresh. Only you can
          see them, and your admin only sees that the{" "}
          {single ? "day is" : "days are"} unreleased.
        </p>
      </div>
      {open.length ? (
        <div className="border-t border-line-2">
          {open.slice(0, MAX_ROWS).map((e, i) => (
            <ReviewRow
              key={e.id}
              entry={e}
              first={i === 0}
              selectable={false}
              checked={false}
            />
          ))}
          {open.length > MAX_ROWS ? (
            <p className="border-t border-line-2 px-3.5 py-2 text-sm text-fg-3">
              and {open.length - MAX_ROWS} more
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center gap-2 border-t border-line-2 p-3">
        <Button
          variant="primary"
          size="sm"
          onClick={onReview}
          data-testid="carry-over-review"
        >
          Review now
          <ArrowRight />
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!ready.length}
          onClick={() => void timeActions.release(ready)}
          data-testid="carry-over-release"
        >
          <Check />
          Release all ready · {ready.length}
          {ready.length ? ` · ${formatHours(totalHours(ready))}` : ""}
        </Button>
        <span className="flex-1" />
        <span className="text-sm text-fg-3">
          This stays here until it's released.
        </span>
      </div>
    </div>
  );
};
